/**
 * Store Pinia des stocks (composition API)
 */
import { defineStore } from "pinia";
import { ref, computed } from "vue";
import StockService from "../services/StockService";
import { IStock } from "../models/stock";
import { IStockForm } from "../models/forms/stockForm";
import { IStockApiResponse } from "../models/api/stockApiResponse";
import { IStockFilter } from "../models/filters/stockFilter";
import { ServiceOperationResult } from "../models/api/serviceOperationResult";
import { IDataOperationResult } from "../models/api/dataOperationResult";
import DataOperationError from "../models/api/dataOperationError";
import { ICenter } from "../models/center";
import { IStockUnity } from "../models/stockUnity";
import { GroupedStock } from "../helper/type";
import { concentrationCovers } from "../helper/concentrationHelper";
import { useCenterStore } from "./center.store";
import { useStockUnityStore } from "./stockUnity.store";
import { useCodeStockStore } from "./codeStock.store";
import signalRManager from "../helper/signalRManager";
import i18n from "@/i18n";
import IPayloadStore from "../models/api/payloadStore";

export const useStockStore = defineStore("stocks", () => {
    // State
    const datas = ref<IStock[]>([]);
    const current = ref<IStock>({} as IStock);
    const itemForm = ref<IStockForm>({} as IStockForm);
    const isLoading = ref<boolean>(false);
    const lastMajStockAt = ref<number>(0);

    // Getters
    // Memoization du groupage : la version anterieure renvoyait une fonction depuis
    // un computed, donc le reduce sur tout `datas` etait re-execute a CHAQUE acces
    // (toggle isByCodeSchema, ouverture de modale, etc.). On materialise les deux modes
    // en deux computed distincts, lazily recomputed seulement quand `datas` change.
    function buildGroupedStocks(bFilterByCodeSchema: boolean, bOnlySiteCodeStock: boolean = false): GroupedStock[] {
        const codeStockStore = useCodeStockStore();
        const filteredStocks = datas.value.filter(
            (stock) => stock.unity && !stock.unity.article?.isMaterial && stock.unity.article?.code
                // Arbitrage 22/09/2026 : les codes stocks autres que SITE sont séparés des besoins
                // et ne comptent pas dedans. L'orientation chargeait le stock du centre sans filtre
                // (stockStore.load({ centerId })) et additionnait donc les dépôts DEPOT/COMMERCIAL,
                // alors que la consultation n'affiche qu'un code stock à la fois : 2 doses d'ALWHF76
                // rangées dans un dépôt commercial apparaissaient dans les besoins, introuvables en
                // gestion de stock. Les écrans de stock, eux, continuent de tout voir.
                && (!bOnlySiteCodeStock || (stock.codeStock != null && codeStockStore.isSiteType(stock.codeStock)))
        );

        const groupedStocks = filteredStocks.reduce((acc: { [key: string]: GroupedStock }, stock) => {
            // Perf : les dates arrivent de l'API sous forme de string ISO ("YYYY-MM-DDT...").
            // substring(0,10) évite l'allocation d'un objet Date à chaque item du reduce.
            const prodDay = stock.unity.productionDate ? (stock.unity.productionDate as unknown as string).substring(0, 10) : "";
            // REQ — Le manufactoringUID distingue deux fabrications distinctes (ex: même article/jour
            // fabriqué en 2x) : sans lui, elles se retrouvaient cumulées dans une seule ligne groupée
            // alors que la consultation des stocks les affiche séparément (une ligne par stockUnity).
            const manufactoringUID = stock.unity?.manufactoringUID ?? "";
            const key = bFilterByCodeSchema
                ? `${stock.unity.isArmada}-${stock.unity.heterospermie}-${stock.unity.conditionnement?.value}-${stock.unity?.article?.geneticType?.code3}-${stock.unity?.volumeDose}-${stock.unity?.spzDose}-${prodDay}-${manufactoringUID}`
                : `${stock.unity.article.code}-${stock.unity.isArmada}-${stock.unity.heterospermie}-${stock.unity.conditionnement?.value}-${stock.unity?.article?.geneticType?.code3}-${stock.unity?.volumeDose}-${stock.unity?.spzDose}-${prodDay}-${manufactoringUID}`;

            if (!acc[key]) {
                acc[key] = {
                    codeArticle: stock.unity.article.code,
                    idArticle: stock.unity.article.id,
                    libelleArticle: stock.unity.article.libelle,
                    centerCode: stock.center?.code,
                    infoCenter: stock.unity.infoCenter,
                    isArmada: stock.unity.isArmada ?? false,
                    isHeterospermie: Boolean(stock.unity.heterospermie),
                    conditionnement: stock.unity.conditionnement?.value,
                    conditionnementCode: stock.unity.conditionnement?.code3,
                    schemaCode: stock.unity?.article?.geneticType?.code3,
                    volumeDose: stock.unity.volumeDose,
                    spzDose: stock.unity.spzDose,
                    stockUnity: stock.unity,
                    quantity: 0,
                    stocks: [],
                    isPrevisional: false,
                };
            }
            acc[key].quantity += stock.quantity;
            acc[key].stocks.push(stock);
            return acc;
        }, {});

        return Object.values(groupedStocks);
    }

    const stockSemenceByArticle = computed(() => buildGroupedStocks(false));
    const stockSemenceBySchema = computed(() => buildGroupedStocks(true));
    /** Stock retenu par les BESOINS (orientation, fabrication) : site de production seulement. */
    const stockSemenceSiteByArticle = computed(() => buildGroupedStocks(false, true));

    function getListeStockSemence(bFilterByCodeSchema: boolean = false): GroupedStock[] {
        return bFilterByCodeSchema ? stockSemenceBySchema.value : stockSemenceByArticle.value;
    }

    // Actions - Hydration
    /**
     * Hydrate une liste de stocks en batch (optimisé pour la performance)
     * - Charge centerStore et stockUnityStore si nécessaires
     * - Crée des Maps d'index pour des lookups O(1)
     */
    async function hydrateStocks(items: IStockApiResponse[]): Promise<IStock[]> {
        if (items.length === 0) return [];

        // 1. Récupérer les stores dépendants
        const centerStore = useCenterStore();
        const stockUnityStore = useStockUnityStore();
        const codeStockStore = useCodeStockStore();

        // 2. Charger les stores dépendants en parallèle si nécessaire
        const loadPromises: Promise<unknown>[] = [];

        if (centerStore.datas.length === 0) {
            loadPromises.push(centerStore.load({ page: 1, pageSize: 0 }));
        }
        if (stockUnityStore.datas.length === 0) {
            loadPromises.push(stockUnityStore.load({ page: 1, pageSize: 0 }));
        }
        if (codeStockStore.datas.length === 0) {
            loadPromises.push(codeStockStore.load({ page: 1, pageSize: 0 }));
        }

        if (loadPromises.length > 0) {
            await Promise.all(loadPromises);
        }

        // 3. Créer des Maps pour des lookups O(1)
        const centerMap = new Map(centerStore.datas.map(c => [c.id, c]));
        let stockUnityMap = new Map(stockUnityStore.datas.map(u => [u.id, u]));
        const codeStockMap = new Map(codeStockStore.datas.map(cs => [cs.id, cs]));

        // Les stores ci-dessus ne sont charges QUE s'ils sont vides. Une unite creee APRES le
        // chargement initial de la session (reception inter-centres, fabrication du jour) en est
        // donc absente : son stock perdait sa navigation `unity` et se faisait ecarter en silence
        // par buildGroupedStocks (qui exige `stock.unity`), le rendant invisible dans les besoins
        // jusqu'au prochain rechargement complet de la page (Mantis 0028215).
        // On ne recharge que si un trou est constate : cout nul dans le cas nominal.
        const unityIdsManquants = new Set(
            items.map(i => i.unityId).filter(id => id != null && !stockUnityMap.has(id))
        );
        if (unityIdsManquants.size > 0) {
            await stockUnityStore.load({ page: 1, pageSize: 0 });
            stockUnityMap = new Map(stockUnityStore.datas.map(u => [u.id, u]));
        }

        // 4. Hydrater tous les stocks en une seule passe
        const models: IStock[] = [];
        for (const item of items) {
            const model = { ...item } as unknown as IStock;

            // Lier les références (lookup O(1) via Map)
            model.center = centerMap.get(item.centerId)!;
            model.unity = stockUnityMap.get(item.unityId)!;
            if (!model.unity) {
                // Sans unite, ce stock sera filtre par buildGroupedStocks : le signaler plutot
                // que de le laisser disparaitre sans trace.
                console.warn(`[stock] Unite ${item.unityId} introuvable : le stock ${item.id} sera ignore dans les besoins.`);
            }
            model.codeStock = codeStockMap.get(item.codeStockId);

            models.push(model);
        }

        return models;
    }

    // Actions

    async function resetStock(centerCodeSpecies: string) {
        isLoading.value = true;
        const result = await StockService.resetStock(centerCodeSpecies);
        isLoading.value = false;
        datas.value = [];
        return result;
    }

    /**
     * La dose en stock couvre-t-elle la concentration exigée ? Comparée au centième, comme les
     * compteurs (concentrationCovers). L'arrondi à l'entier supérieur laissait une dose à 32.5
     * couvrir une commande à 33 (Mantis 0028557).
     */
    function stockCoversConcentration(group: GroupedStock, concentrationDose: number): boolean {
        return concentrationCovers((group.spzDose ?? 0) / (group.volumeDose ?? 1), concentrationDose);
    }

    function getStock(schemaCode: string, codeConditionnement: string, armada: boolean, heterospermie: boolean, concentrationDose: number, volume: number) {
        // Besoins : site de production seulement (arbitrage 22/09/2026, cf. buildGroupedStocks).
        const specificStocks = stockSemenceSiteByArticle.value;

        const listSpecificStocks = specificStocks.filter(
            (group) =>
                group.codeArticle.startsWith(schemaCode) &&
                group.conditionnementCode === codeConditionnement &&
                group.isHeterospermie === heterospermie &&
                group.isArmada === armada &&
                stockCoversConcentration(group, concentrationDose) &&
                group.volumeDose == volume
        );

        var totalQuantity = listSpecificStocks.reduce((sum, group) => sum -= group.quantity, 0);

        return {
            total: totalQuantity,
            stocks: listSpecificStocks,
        };
    }

    function getStockHeterospermie(schemaCode: string, armada: boolean, volume: number, concentrationDose: number) {
        const specificStocks = stockSemenceSiteByArticle.value;

        const listSpecificStocks = specificStocks.filter(
            (group) =>
                group.codeArticle.startsWith(schemaCode) &&
                group.conditionnementCode != 'H' &&
                group.isHeterospermie == true &&
                stockCoversConcentration(group, concentrationDose) &&
                group.isArmada === armada &&
                group.volumeDose == volume
        );

        var totalQuantity = listSpecificStocks.reduce((sum, group) => sum -= group.quantity, 0);

        return {
            total: totalQuantity,
            stocks: listSpecificStocks,
        };
    }

    function getSpecificStock(schemaCode: string, codeConditionnement: string, armada: boolean, heterospermie: boolean, codearticle: string, concentrationDose: number, volume: number, isStandard: boolean, isArticleCheck: boolean, hasOtherOtherOrder: boolean) {
        const specificStocks = stockSemenceSiteByArticle.value;

        const listSpecificStocks = specificStocks.filter(
            (group) =>
                group.codeArticle.startsWith(schemaCode) &&
                group.conditionnementCode === codeConditionnement &&
                group.isArmada === armada &&
                group.isHeterospermie === heterospermie &&
                (Math.ceil(((group.spzDose ?? 0) / (group.volumeDose ?? 1))) >= concentrationDose) &&
                (!isArticleCheck || group.codeArticle === codearticle)
        );

        var totalQuantity = listSpecificStocks.reduce((sum, group) => sum -= group.quantity, 0);

        return {
            total: totalQuantity,
            stocks: listSpecificStocks,
        };
    }

    /**
     * Chargement des données dans le store
     */
    async function load(filter: IStockFilter) {
        isLoading.value = true;
        const result = await StockService.getAll<IStockFilter, IStockApiResponse>(filter);
        isLoading.value = false;
        datas.value = [];

        if (!result.error) {
            datas.value = await hydrateStocks(result.data as IStockApiResponse[]);
        }

        return result;
    }

    /**
     * Récupération d'un stock par son identifiant
     */
    async function get(id: number) {
        const result = await StockService.getById<IStockApiResponse>(id);

        if (!result.error) {
            const [hydratedItem] = await hydrateStocks([result.data as IStockApiResponse]);

            const index = datas.value.findIndex((a) => a.id == id);
            if (index >= 0)
                datas.value[index] = hydratedItem;

            current.value = hydratedItem;
            itemForm.value = toForm(hydratedItem);
        }

        return result;
    }

    /**
     * Récupération de l'objet dans le store
     */
    async function searchStoreById(id: number): Promise<IStock> {
        current.value = datas.value.find((objet) => objet.id == id)!;
        itemForm.value = toForm(current.value as IStock);
        return current.value;
    }

    /**
     * Mise à jour ou création d'un objet
     */
    async function save(maj: boolean = true, isPatch: boolean = false) {
        let result = {} as ServiceOperationResult<IStock | IDataOperationResult<IStock>>;

        if (itemForm.value.id > 0) {
            result = await StockService.update(itemForm.value);

            if (!result.error) {
                const [hydratedItem] = await hydrateStocks([result.data as unknown as IStockApiResponse]);
                current.value = hydratedItem;

                if (maj) {
                    const index = datas.value.findIndex((a) => a.id == itemForm.value.id);
                    if (index >= 0) {
                        datas.value[index] = current.value;
                    } else {
                        const error = new DataOperationError();
                        error.REQUEST_ERROR = i18n.global.t("errors.itemNotFoundInStore");
                        return new ServiceOperationResult<IDataOperationResult<IStock>>(true, { errors: error });
                    }
                }
            }
        } else {
            result = await StockService.create(itemForm.value);

            if (!result.error) {
                const [hydratedItem] = await hydrateStocks([result.data as unknown as IStockApiResponse]);
                current.value = hydratedItem;
                if (datas.value.findIndex((a) => a.id == current.value?.id) == -1) {
                    datas.value.push(current.value);
                }
            }
        }

        return result;
    }

    /**
     * Delete de l'objet
     */
    async function deleteAction() {
        const result = await StockService.delete<IStockApiResponse>(current.value.id);

        if (!result.error) {
            const index = datas.value.findIndex((a) => a.id == current.value.id);
            if (index >= 0) {
                datas.value.splice(index, 1);
            }

            current.value = newItem({ id: 0, name: "", code: "", isActive: true } as ICenter);
            itemForm.value = newForm(0);
        }

        return result;
    }

    /**
     * Transformation de l'objet en formulaire
     */
    function toForm(item: IStock): IStockForm {
        const form = {} as IStockForm;
        form.id = item.id;
        form.codeStockId = item.codeStockId;
        form.centerId = item.center.id;
        form.quantity = item.quantity;
        form.unityId = item.unity.id;
        itemForm.value = form;
        return form;
    }

    /**
     * Permet d'initialiser un objet vide pour le formulaire
     */
    function newForm(lienId: number): IStockForm {
        itemForm.value = {
            id: 0,
            centerId: lienId,
            codeStockId: 0,
            quantity: 0,
            unityId: 0
        } as IStockForm;
        return itemForm.value;
    }

    /**
     * Permet d'initialiser un objet vide pour les referentiels
     */
    function newItem(lien: ICenter): IStock {
        current.value = {
            id: 0,
            codeStockId: 0,
            quantity: 0,
            center: lien,
            unity: { id: 0 } as IStockUnity,
        } as IStock;
        itemForm.value = toForm(current.value);
        return current.value;
    }

    return {
        // State
        datas,
        current,
        itemForm,
        isLoading,
        lastMajStockAt,
        // Getters
        getListeStockSemence,
        // Actions
        hydrateStocks,
        resetStock,
        getStock,
        getStockHeterospermie,
        getSpecificStock,
        load,
        get,
        searchStoreById,
        save,
        delete: deleteAction,
        toForm,
        newForm,
        newItem,
    };
});

// Perf SignalR : les broadcasts MajStock arrivent en rafale lors d'une mise en stock en lot
// (ex. 200 doses validées d'un coup). Sans debounce, chaque message déclencherait un appel
// hydrateStocks indépendant (rechargement des stores dépendants + lookups Maps × N fois).
// On bufferise sur 200 ms puis on traite tout le batch en un seul appel hydrateStocks.
const _majStockBuffer: IPayloadStore[] = [];
let _majStockTimer: ReturnType<typeof setTimeout> | null = null;

async function _flushMajStock() {
    const batch = _majStockBuffer.splice(0);
    _majStockTimer = null;
    if (batch.length === 0) return;

    const store = useStockStore();

    // Regrouper tous les Create/Update en un seul appel hydrateStocks
    const toHydrate = batch
        .filter(p => p.action === "Create" || p.action === "Update")
        .map(p => p.data as IStockApiResponse)
        .filter(Boolean);

    const hydratedMap = new Map<number, IStock>();
    if (toHydrate.length > 0) {
        const hydrated = await store.hydrateStocks(toHydrate);
        hydrated.forEach(h => hydratedMap.set(h.id, h));
    }

    for (const payload of batch) {
        switch (payload.action) {
            case "Create": {
                if (store.datas.findIndex(a => a.id == payload.data?.id) === -1) {
                    const h = hydratedMap.get(payload.data?.id as number);
                    if (h) store.datas.push(h);
                }
                break;
            }
            case "Update": {
                const idx = store.datas.findIndex(a => a.id == payload.data?.id);
                if (idx > -1) {
                    const h = hydratedMap.get(payload.data?.id as number);
                    if (h) store.datas.splice(idx, 1, h);
                }
                break;
            }
            case "Delete": {
                const idx = store.datas.findIndex(a => a.id == payload.id);
                if (idx > -1) store.datas.splice(idx, 1);
                break;
            }
        }
    }
    store.lastMajStockAt = Date.now();
}

signalRManager.connection.on("MajStock", (payload: IPayloadStore) => {
    _majStockBuffer.push(payload);
    if (_majStockTimer) clearTimeout(_majStockTimer);
    _majStockTimer = setTimeout(_flushMajStock, 200);
});
